const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

interface MeditationData {
  id?: string;
  userId?: string;
  date?: string;
  verseReference: string;
  verseText: string;
  readingCount: number;
  godsWill?: string;
  growthChange?: string;
  prayer?: string;
  step1Complete: boolean;
  step2Complete: boolean;
  step3Complete: boolean;
  step4Complete: boolean;
  prayerShareType?: string;
  createdAt?: string;
  updatedAt?: string;
  // 피드용 추가 필드들
  user?: {
    id: string;
    name: string;
    avatar?: string;
    email: string;
  };
  crew?: string;
  time?: string;
  content?: string;
  likes?: number;
  comments?: number;
  verse?: string; // 말씀 텍스트 (verseText의 별칭)
}

interface MeditationResponse {
  success: boolean;
  meditation: MeditationData;
  completedStep?: number;
}

interface MeditationHistoryResponse {
  meditations: MeditationData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class MeditationAPI {
  private getAuthHeader() {
    const token = localStorage.getItem('accessToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // 오늘의 묵상 불러오기
  async getTodayMeditation(): Promise<MeditationData> {
    try {
      const response = await fetch(`${API_URL}/meditations/today`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('[API] /meditations/today', response.status, text);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching today meditation:', error);
      throw error;
    }
  }

  // 묵상 데이터 저장/업데이트
  async saveMeditation(data: Partial<MeditationData>): Promise<MeditationResponse> {
    try {
      const response = await fetch(`${API_URL}/meditations/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error saving meditation:', error);
      throw error;
    }
  }

  // 특정 단계 완료 처리
  async completeStep(step: number, data: any): Promise<MeditationResponse> {
    try {
      console.log('🔍 meditationAPI.completeStep 호출:', { step, data, API_URL });
      
      const authHeader = this.getAuthHeader();
      console.log('🔑 인증 헤더:', authHeader);
      
      const response = await fetch(`${API_URL}/meditations/complete-step`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader,
        },
        body: JSON.stringify({ step, data }),
      });

      console.log('📡 API 응답 상태:', response.status, response.statusText);

      if (!response.ok) {
        const text = await response.text();
        console.error('[API] /meditations/complete-step 실패:', response.status, text);
        throw new Error(`HTTP error! status: ${response.status} - ${text}`);
      }

      const result = await response.json();
      console.log('✅ API 응답 성공:', result);
      return result;
    } catch (error) {
      console.error('❌ Error completing meditation step:', error);
      throw error;
    }
  }

  // 묵상 히스토리 조회
  async getMeditationHistory(page = 1, limit = 10): Promise<MeditationHistoryResponse> {
    try {
      console.log('🔍 meditationAPI.getMeditationHistory 호출:', { page, limit, API_URL });
      
      const authHeader = this.getAuthHeader();
      console.log('🔑 인증 헤더:', authHeader);
      
      const response = await fetch(`${API_URL}/meditations/history?page=${page}&limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader,
        },
      });

      console.log('📡 API 응답 상태:', response.status, response.statusText);

      if (!response.ok) {
        const text = await response.text();
        console.error('[API] /meditations/history 실패:', response.status, text);
        throw new Error(`HTTP error! status: ${response.status} - ${text}`);
      }

      const result = await response.json();
      console.log('✅ API 응답 성공:', result);
      return result;
    } catch (error) {
      console.error('❌ Error fetching meditation history:', error);
      throw error;
    }
  }

  // 공유된 묵상 피드 조회
  async getMeditationFeed(page = 1, limit = 20, scope?: 'my-crews'): Promise<MeditationHistoryResponse> {
    try {
      console.log('🔍 meditationAPI.getMeditationFeed 호출:', { page, limit, API_URL });
      
      const authHeader = this.getAuthHeader();
      console.log('🔑 인증 헤더:', authHeader);
      
      const scopeParam = scope ? `&scope=${scope}` : '';
      const response = await fetch(`${API_URL}/meditations/feed?page=${page}&limit=${limit}${scopeParam}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader,
        },
      });

      console.log('📡 API 응답 상태:', response.status, response.statusText);

      if (!response.ok) {
        const text = await response.text();
        console.error('[API] /meditations/feed 실패:', response.status, text);
        throw new Error(`HTTP error! status: ${response.status} - ${text}`);
      }

      const result = await response.json();
      console.log('✅ API 응답 성공:', result);
      console.log('📱 피드 데이터 상세:', result.meditations?.map((m: any) => ({
        id: m.id,
        user: m.user?.name,
        createdAt: m.createdAt,
        updatedAt: m.updatedAt,
        date: m.date
      })));
      return result;
    } catch (error) {
      console.error('❌ Error fetching meditation feed:', error);
      throw error;
    }
  }

  // 공개 단건 조회 (로그인 없이 접근 가능)
  async getPublicMeditation(id: string): Promise<MeditationData> {
    try {
      console.log('🔍 meditationAPI.getPublicMeditation 호출:', { id, API_URL });
      const response = await fetch(`${API_URL}/meditations/public/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('[API] /meditations/public/:id 실패:', response.status, text);
        throw new Error(`HTTP error! status: ${response.status} - ${text}`);
      }

      const result = await response.json();
      if (!result?.success || !result?.meditation) {
        throw new Error('Invalid response');
      }

      const m = result.meditation;
      const mapped: MeditationData = {
        id: m.id,
        user: m.user,
        verseReference: m.verseReference,
        verseText: m.verseText,
        godsWill: m.godsWill ?? undefined,
        growthChange: m.growthChange ?? undefined,
        prayer: m.prayer ?? undefined,
        step1Complete: true,
        step2Complete: true,
        step3Complete: true,
        step4Complete: true,
        readingCount: 0,
        createdAt: m.createdAt,
        updatedAt: m.updatedAt,
      };
      return mapped;
    } catch (error) {
      console.error('❌ Error fetching public meditation:', error);
      throw error;
    }
  }

  // 미완료 묵상 기록 삭제
  async deleteIncompleteMeditations(): Promise<{ message: string; deletedCount: number }> {
    try {
      console.log('📤 미완료 묵상 삭제 API 호출');
      
      const response = await fetch(`${API_URL}/meditations/incomplete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('[API] DELETE /meditations/incomplete', response.status, text);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ 미완료 묵상 삭제 API 성공:', result);
      return result;
    } catch (error) {
      console.error('❌ 미완료 묵상 삭제 API 실패:', error);
      throw error;
    }
  }

  // 특정 묵상 기록 삭제
  async deleteMeditation(meditationId: string): Promise<{ success: boolean; message: string; deletedId: string }> {
    try {
      console.log('📤 묵상 삭제 API 호출:', meditationId);

      const response = await fetch(`${API_URL}/meditations/${meditationId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('[API] DELETE /meditations/:id', response.status, text);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ 묵상 삭제 API 성공:', result);
      return result;
    } catch (error) {
      console.error('❌ 묵상 삭제 API 실패:', error);
      throw error;
    }
  }

  // 묵상 수정
  async updateMeditation(meditationId: string, meditationData: Partial<MeditationData>): Promise<{ success: boolean; meditation: MeditationData }> {
    try {
      console.log('📤 묵상 수정 API 호출:', meditationId, meditationData);

      const response = await fetch(`${API_URL}/meditations/${meditationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
        body: JSON.stringify(meditationData),
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('[API] PUT /meditations/:id', response.status, text);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ 묵상 수정 API 성공:', result);
      return result;
    } catch (error) {
      console.error('❌ 묵상 수정 API 실패:', error);
      throw error;
    }
  }
}

export const meditationAPI = new MeditationAPI();
export type { MeditationData, MeditationResponse, MeditationHistoryResponse };
